import { Injectable } from "@angular/core";
import type jsPDF from "jspdf";
import type { Styles, UserOptions } from "jspdf-autotable";

type ContentType = 'text' | 'image';
type Alignment = 'left' | 'center' | 'right';

interface ContentOptions {
    type?: ContentType;
    fontSize?: number;
    bold?: boolean;
    align?: Alignment;
    width?: number;
    height?: number;
    spacing?: number;
    sameLine?: boolean;
}

interface TableOptions extends UserOptions {
  repeatTHead?: boolean;
}

@Injectable({
    providedIn: "root",
})
export class PdfExportService {
    async createDocument(orientation: "portrait" | "landscape" = "portrait"): Promise<jsPDF> {
        const { default: jsPDF } = await import(/* webpackChunkName: "jspdf" */ 'jspdf');
        return new jsPDF(orientation, "pt", "a4");
    }

    private getLastPosition(doc: jsPDF): number {
        const lastTable = (doc as any).lastAutoTable;
        let lastY = lastTable ? lastTable.finalY : 20;
        const storedY = (doc as any).__lastContentY;
        if (storedY) lastY = Math.max(lastY, storedY);
        return lastY;
    }

    private updateLastPosition(doc: jsPDF, y: number) {
        (doc as any).__lastContentY = y;
    }

    private isSameLineAvailable(doc: jsPDF): boolean {
        return (doc as any).__sameLineAvailable === true;
    }

    private setSameLineAvailable(doc: jsPDF, available: boolean) {
        (doc as any).__sameLineAvailable = available;
    }

    addTitle(doc: jsPDF, title: string, subtitle?: string) {
        const lastY = this.getLastPosition(doc) + 20;
        const pageWidth = doc.internal.pageSize.getWidth();
        doc.setFontSize(18);
        doc.setFont("helvetica", "bold");
        doc.text(title, pageWidth / 2, lastY + 20, { align: "center" });

        if (subtitle) {
            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");
            doc.text(subtitle, pageWidth / 2, lastY + 40, { align: "center" });
            this.updateLastPosition(doc, lastY + 50);
        } else {
            this.updateLastPosition(doc, lastY + 30);
        }
        this.setSameLineAvailable(doc, false);
    }

    async addTable(
        doc: jsPDF,
        headers: string[],
        body: (string | { content: string; styles?: Partial<Styles> })[][],
        options: Partial<TableOptions> = {}
    ): Promise<void> {
        const startY = options.startY || this.getLastPosition(doc) + 20;
        const repeatTHead = options.repeatTHead !== false; // Default true unless explicitly set to false

        const defaultHeadStyles: Partial<Styles> = {
            fillColor: [230, 240, 255],
            textColor: 30,
            fontSize: 10,
            halign: "center",
        };

        const mergedHeadStyles = {
            ...defaultHeadStyles,
            ...(options.headStyles || {})
        };

        const { default: autoTable } = await import(/* webpackChunkName: "jspdf" */ 'jspdf-autotable');
        autoTable(doc, {
            startY,
            head: [headers],
            body,
            theme: options.theme || "grid",
            headStyles: mergedHeadStyles,
            bodyStyles: options.bodyStyles || {
                fontSize: 9,
                cellPadding: 4,
                halign: "left",
                valign: "top",
            },
            styles: { overflow: "linebreak" },
            columnStyles: options.columnStyles || {},
            didDrawCell: options.didDrawCell ? options.didDrawCell : undefined,
            pageBreak: 'auto',
            showHead: repeatTHead ? 'everyPage' : 'firstPage'
        });
        this.setSameLineAvailable(doc, false);
    }

    addLegend(
        doc: jsPDF,
        legend: { label: string; color: string }[],
        startY?: number
    ) {
        const baseY = startY ?? this.getLastPosition(doc) + 20;
        const squareSize = 12;
        const gapX = 10;
        const itemSpacing = 80;
        const maxX = doc.internal.pageSize.getWidth() - 40;

        let x = 40;
        let y = baseY;

        legend.forEach((r) => {
            if (x + itemSpacing > maxX) {
                x = 40;
                y += 30;
            }

            const [rC, gC, bC] = this.toRgbArray(r.color);
            doc.setFillColor(rC, gC, bC);
            doc.rect(x, y, squareSize, squareSize, "F");

            doc.setFontSize(11);
            doc.text(r.label, x + squareSize + gapX, y + squareSize - 2);

            x += itemSpacing;
        });
        this.updateLastPosition(doc, y + 40);
        this.setSameLineAvailable(doc, false);
    }

    addContent(
        doc: jsPDF,
        content: string,
        options: ContentOptions = {}
    ) {
        const isSameLine = options.sameLine && this.isSameLineAvailable(doc);
        const lastY = isSameLine ? this.getLastPosition(doc) - 20 : this.getLastPosition(doc) + (options.spacing ?? 12);
        const pageWidth = doc.internal.pageSize.getWidth();
        const type = options.type || 'text';
        const align = options.align || 'left';

        let x = 40;
        if (align === 'center') {
            x = pageWidth / 2;
        } else if (align === 'right') {
            x = pageWidth - 40;
        }

        if (type === 'image') {
            const imgWidth = options.width || 60;
            const imgHeight = options.height || 30;

            if (align === 'center') {
                x = (pageWidth - imgWidth) / 2;
            } else if (align === 'right') {
                x = pageWidth - imgWidth - 40;
            }

            try {
                doc.addImage(content, x, lastY, imgWidth, imgHeight);
                this.updateLastPosition(doc, isSameLine ? lastY + 20 : lastY + imgHeight);
            } catch (error) {
                this.updateLastPosition(doc, isSameLine ? lastY + 20 : lastY);
            }
        } else {
            if (options.bold) doc.setFont("helvetica", "bold");
            doc.setFontSize(options.fontSize ?? 11);
            doc.text(content, x, lastY, { align });
            doc.setFont("helvetica", "normal");
            this.updateLastPosition(doc, lastY);
        }

        this.setSameLineAvailable(doc, options.sameLine || false);
    }

    saveDocument(doc: jsPDF, filename: string) {
        doc.save(filename);
    }

    toRgbArray(color: string): [number, number, number] {
        if (!color) return [200, 200, 200];

        if (color.startsWith("#")) {
            let hex = color.replace("#", "");
            if (hex.length === 3) {
                hex = hex.split("").map(c => c + c).join("");
            }

            let alpha = 1;
            if (hex.length === 8) {
                alpha = parseInt(hex.substring(6, 8), 16) / 255;
                hex = hex.substring(0, 6);
            }

            const bigint = parseInt(hex, 16);
            const r = (bigint >> 16) & 255;
            const g = (bigint >> 8) & 255;
            const b = bigint & 255;
            return this.applyAlpha(r, g, b, alpha);
        }

        if (color.startsWith("rgb")) {
            const nums = color.replace(/[rgba()]/g, "")
                .split(",").map((n) => parseFloat(n.trim()));

            const [r, g, b] = nums;
            const alpha = nums[3] ?? 1;
            return this.applyAlpha(r, g, b, alpha);
        }

        return [200, 200, 200];
    }

    private applyAlpha(r: number, g: number, b: number, a: number): [number, number, number] {
        const background = 255; // white
        return [
            Math.round(r * a + background * (1 - a)),
            Math.round(g * a + background * (1 - a)),
            Math.round(b * a + background * (1 - a))
        ];
    }

    drawBarcodeInCell(doc: jsPDF, displayValue: string, imgData: string, cellX: number, cellY: number, cellWidth: number, cellHeight: number): void {
        // Clear cell with white background
        doc.setFillColor(255, 255, 255);
        doc.rect(cellX + 1, cellY + 1, cellWidth - 2, cellHeight - 2, 'F');

        // Draw display value text with larger font for wider cells
        const fontSize = cellWidth > 100 ? 11 : 10;
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        const displayValueX = cellX + cellWidth / 2;
        const displayValueY = cellY + fontSize + 2;
        doc.text(displayValue, displayValueX, displayValueY, { align: 'center' });

        // Add barcode image - scale with cell width
        try {
            const barcodeWidth = Math.min(cellWidth - 10, Math.max(cellWidth * 0.85, 70));
            const barcodeHeight = cellWidth > 100 ? 35 : 22;
            const barcodeX = cellX + (cellWidth - barcodeWidth) / 2;
            const barcodeY = cellY + fontSize + 8;
            doc.addImage(imgData, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
        } catch (error) {
        }
    }

    async generateBarcodeImageData(barcodeContent: string): Promise<string> {
        if (!barcodeContent) {
            throw new Error('No barcode content');
        }

        const { default: JsBarcode } = await import(/* webpackChunkName: "jsbarcode" */ 'jsbarcode');

        return new Promise((resolve, reject) => {
            let svg: SVGSVGElement | null = null;
            let url: string | null = null;

            const cleanup = () => {
                try {
                    if (svg && svg.parentNode) { svg.parentNode.removeChild(svg); }
                } catch {}
                try {
                    if (url) { URL.revokeObjectURL(url); }
                } catch {}
                svg = null;
                url = null;
            };

            try {
                // Create temporary SVG
                svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svg.setAttribute('style', 'display:none');
                document.body.appendChild(svg);

                // Generate barcode in SVG
                JsBarcode(svg, barcodeContent, {
                    format: 'CODE128',
                    lineColor: '#000',
                    displayValue: true,
                    width: 2,
                    height: 50,
                    fontSize: 14,
                });

                // Convert SVG to image data
                const svgData = new XMLSerializer().serializeToString(svg);
                const blob = new Blob([svgData], { type: 'image/svg+xml' });
                url = URL.createObjectURL(blob);

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                const img = new Image();
                img.onload = () => {
                    try {
                        canvas.width = img.naturalWidth || img.width;
                        canvas.height = img.naturalHeight || img.height;

                        ctx?.drawImage(img, 0, 0);
                        const imgDataUrl = canvas.toDataURL('image/png');
                        cleanup();
                        resolve(imgDataUrl);
                    } catch (err) {
                        cleanup();
                        reject(err);
                    }
                };

                img.onerror = () => {
                    cleanup();
                    reject('Failed to load barcode image');
                };

                img.src = url;
            } catch (error) {
                cleanup();
                reject(error);
            }
        });
    }
}
